import { useMutation } from "@apollo/client";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Stack,
} from "@mui/material";
import Paper from "@mui/material/Paper";
import { useEffect, useState } from "react";
import { LinkButton } from "../../../components/LinkButton";
import { PageContainer } from "../../../components/PageContainer";
import { Band, Post } from "../../../__generated__/graphql";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import * as yup from "yup";
import { ArtistLinkTypePicker } from "../../../components/ArtistLinkTypePicker";
import { BandPicker } from "../../../components/BandPicker";
import { VendorPicker } from "../../../components/VendorPicker";
import { GET_BAND, UPDATE_BAND_LINKS } from "../graphql";
import { toast } from "react-toastify";
import { GET_POST, UPDATE_POST_LINKS } from "../../post/graphql";

interface IPostLinks {
  post: Post;
  disableContainer?: boolean;
}

export interface IArtistLink {
  type: "artist" | "vendor";
  linkedItemName: string;
  linkedId: string;
  description: string;
}

const schema = yup
  .object({
    links: yup
      .array()
      .of(
        yup.object({
          type: yup.string().required(),
          linkedItemName: yup.string().required(),
          linkedId: yup.string().required(),
          description: yup.string().required(),
        })
      )
      .required(),
  })
  .required();

const PostLinksList = ({ post, disableContainer }: IPostLinks) => {
  const links: IArtistLink[] =
    Object.keys(post.links ?? {}).map((key) => {
      const { type, linkedItemName, linkedId, description } = post.links[
        key
      ] as IArtistLink;

      return {
        type,
        linkedId,
        linkedItemName,
        description,
      };
    }) ?? [];

  if (!links) return null;

  const content = <PostLinksForm post={post} links={links} />;

  if (disableContainer) {
    return content;
  }

  return <PageContainer title="Linked Items">{content}</PageContainer>;
};

export interface IPostLinksform {
  post: Post;
  links: IArtistLink[];
}

const PostLinksForm = ({ post, links }: IPostLinksform) => {
  const [forDeletion, updateForDeletion] = useState<string[]>([]);
  const {
    register,
    handleSubmit,
    watch,
    control,
    formState: { errors, dirtyFields },
    reset,
    setValue,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      links: links,
    },
  });

  useEffect(() => {
    reset({
      links: links,
    });
  }, [links]);

  const { fields, replace, prepend, remove, swap, move, insert } =
    useFieldArray({
      control, // control props comes from useForm (optional: if you are using FormContext)
      name: "links", // unique name for your Field Array
    });

  const [updatePost, { loading: updateLoading, error }] = useMutation(
    UPDATE_POST_LINKS,
    {
      onCompleted: () => {
        toast("post updated");
      },
      refetchQueries: [GET_POST],
    }
  );

  const onSubmit = (form: { links: IArtistLink[] }) => {
    const withoutDeleted = form.links.filter(
      (item) => !forDeletion.includes(item.linkedId)
    );

    const ready = withoutDeleted.reduce((acc, next) => {
      // @ts-ignore
      acc[next.linkedId] = next;

      return acc;
    }, {});

    updatePost({
      variables: {
        id: post.id,
        links: ready,
      },
    });
  };

  return (
    // @ts-ignore
    <form onSubmit={handleSubmit(onSubmit)}>
      <Button
        onClick={() =>
          prepend({
            // @ts-ignore
            state: "unsaved",
            type: "artist",
            linkedItemName: "",
            linkedId: "",
            description: "",
          })
        }
        color="success"
        variant="contained"
        style={{ height: "100%" }}
      >
        Add New
      </Button>
      <TableContainer component={Paper} style={{ minHeight: 500 }}>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell align="left">Type</TableCell>
              <TableCell align="left">Linked Item</TableCell>
              <TableCell align="left">Description</TableCell>
              <TableCell align="left">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {fields.map((row, index) => (
              <TableRow
                key={row.id}
                sx={{
                  "&:last-child td, &:last-child th": { border: 0 },
                  "& td": {
                    verticalAlign: "top",
                    py: 2,
                  },
                }}
                style={{ overflow: "visible" }}
              >
                <input
                  type="hidden"
                  defaultValue={row.linkedId ?? ""}
                  {...register(`links.${index}.linkedId`)}
                />
                <TableCell align="left" style={{ overflow: "visible" }}>
                  <Box sx={{ display: "flex", alignItems: "flex-end", width: "100%" }}>
                    <Controller
                      control={control}
                      {...register(`links.${index}.type`)}
                      render={({ field: { onChange, onBlur, value, ref } }) => (
                        <ArtistLinkTypePicker
                          // @ts-ignore
                          handleChange={onChange}
                          defaultValue={value}
                          value={value}
                        />
                      )}
                    />
                  </Box>
                </TableCell>

                {/* <BandPicker
                      // @ts-ignore
                      handleChange={onChange}
                      defaultValue={value}
                      value={value}
                    />
                  )} */}
                <TableCell align="left" style={{ overflow: "visible" }}>
                  <Box sx={{ display: "flex", alignItems: "flex-end", width: "100%" }}>
                    <Controller
                      control={control}
                      {...register(`links.${index}.linkedId`)}
                      render={({ field: { onChange, onBlur, value, ref } }) => {
                        const except = watch("links").map(
                          (item) => item.linkedId
                        );

                        return (
                          <>
                            {watch(`links.${index}.type`) === "artist" && (
                              <BandPicker
                                except={except}
                                controlStyle={{ marginTop: 0, marginBottom: 0 }}
                                handleChange={(value, option) => {
                                  setValue(`links.${index}.linkedId`, value ?? "");

                                  setValue(
                                    `links.${index}.linkedItemName`,
                                    // @ts-ignore
                                    option?.name ?? ""
                                  );
                                  setValue(
                                    `links.${index}.description`,
                                    // @ts-ignore
                                    option?.name ?? "",
                                    {
                                      shouldValidate: true,
                                      shouldDirty: true,
                                      shouldTouch: true
                                    }
                                  );
                                }}
                                defaultValue={value}
                                value={value}
                              />
                            )}

                            {watch(`links.${index}.type`) !== "artist" && (
                              <VendorPicker
                                except={except}
                                placeholder="Select Vendor"
                                controlStyle={{ marginTop: 0, marginBottom: 0 }}
                                handleChange={(value, option) => {
                                  setValue(`links.${index}.linkedId`, value ?? "");

                                  setValue(
                                    `links.${index}.linkedItemName`,
                                    // @ts-ignore
                                    option?.name ?? ""
                                  );

                                  setValue(
                                    `links.${index}.description`,
                                    // @ts-ignore
                                    option?.name ?? ""
                                  );
                                }}
                                defaultValue={value}
                                value={value}
                              />
                            )}
                          </>
                        );
                      }}
                    />
                  </Box>
                </TableCell>
                <TableCell align="left">
                  <Box sx={{ display: "flex", alignItems: "flex-end", width: "100%" }}>
                    <TextField
                      fullWidth
                      id="outlined-basic"
                      label="Description"
                      variant="outlined"
                      InputProps={{
                        sx: { height: 56 },
                      }}
                      InputLabelProps={{
                        shrink: true
                      }}
                      defaultValue={row.description}
                      error={!!errors.links?.[index]?.description?.message}
                      {...register(`links.${index}.description`)}
                    />
                  </Box>
                </TableCell>
                <TableCell align="left">
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-start",
                      width: "100%",
                      minHeight: 56,
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      flexWrap="wrap"
                      rowGap={1}
                    >
                      {row.linkedId && !dirtyFields.links && (
                        <LinkButton
                          path={
                            watch(`links.${index}.type`) === "artist"
                              ? `/band/${row.linkedId}`
                              : `/vendor/${row.linkedId}`
                          }
                          text="View"
                          variant="outlined"
                        />
                      )}
                      <Button
                        disabled={forDeletion.includes(row.linkedId)}
                        onClick={() => {
                          console.log("row", row);
                          // if (row.linkedId === "") {
                          // @ts-ignore
                          if (row.state === "unsaved") {
                            remove(index);
                          } else {
                            updateForDeletion([...forDeletion, row.linkedId]);
                          }
                        }}
                        color="error"
                        variant="contained"
                      >
                        Delete
                      </Button>
                    </Stack>
                  </Box>
                </TableCell>
              </TableRow>
            ))}

            {/* {links.map((row) => (
            <TableRow
              key={row.linkedId}
              sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
            >
              <TableCell align="left">{row.linkedItemName}</TableCell>
              <TableCell align="left">{row.type}</TableCell>
              <TableCell align="left">{row.description}</TableCell>
              <TableCell align="left">
                <LinkButton
                  path={`/band/${band.id}/links/${row.linkedId}/edit`}
                  text="View"
                />
              </TableCell>
            </TableRow>
          ))} */}
          </TableBody>
        </Table>
      </TableContainer>
      <Button
        type="submit"
        variant="contained"
        style={{ marginTop: 18, marginBottom: 18 }}
      >
        Save
      </Button>
    </form>
  );
};

export default PostLinksList;
